import { useDebounce } from "ahooks";
import { AutoComplete, Button, Form, message, Modal, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import { GetListAccount, SysUserRoleService, UserInRole } from "../../../../../services/config/qltl-config/sysUserRole";

interface FormValues {
  userId: string;
}

interface UserRoleFormProps {
  open: boolean;
  roleId: string;
  refresh: () => Promise<void>;
}

const UserRoleForm = (props: UserRoleFormProps) => {
  const { roleId, open, refresh } = props;

  const [userList, setUserList] = useState<UserInRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [userSelected, setUserSelected] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [openModalError, setOpenModalError] = useState(false);

  const [isAddLoading, setIsAddLoading] = useState(false);

  const [form] = Form.useForm<FormValues>();

  const keywordDebounce = useDebounce(keyword, {
    wait: 500,
  });

  const fetchUserList = useCallback(async (params: GetListAccount) => {
    setIsLoading(true);

    try {
      const res = await SysUserRoleService.getListAccount(params);

      setUserList(res || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetForm = useCallback(() => {
    form.resetFields();
    setUserSelected(null);
    setKeyword("");
  }, [form]);

  const handleSubmit = useCallback(
    async (values: FormValues, force?: boolean) => {
      console.log(values);

      if (!values.userId || !userSelected) return;

      const user = userList.find((user) => user.id === userSelected);

      if (!user) return form.setFields([{ name: "userId", errors: ["Người dùng không tồn tại"] }]);

      setIsAddLoading(true);

      try {
        if (!force) {
          const res = await SysUserRoleService.checkExistUserRole({
            roleId,
            userId: userSelected,
          });

          if (res) {
            setModalError(
              `Người dùng ${user.fullName} đã có trong vai trò ${res}. Anh/chị có chắc chắn muốn dổi sang vai trò này không?`,
            );
            setOpenModalError(true);
            setIsAddLoading(false);
            return;
          }
        }

        await SysUserRoleService.createUserRoles({
          roleId,
          userId: userSelected,
        });

        await refresh();
        resetForm();

        message.success("Thêm người dùng thành công");
        setOpenModalError(false);
        setModalError(null);
      } catch (error) {
        message.error((error as Error)?.message || "Thêm người dùng thất bại");
      } finally {
        setIsAddLoading(false);
      }
    },
    [form, refresh, resetForm, roleId, userList, userSelected],
  );

  useEffect(() => {
    fetchUserList({
      keyword: keywordDebounce,
    });
  }, [fetchUserList, keywordDebounce]);

  useEffect(() => {
    resetForm();
  }, [resetForm, open]);

  return (
    <Form form={form} onFinish={handleSubmit}>
      <div className="flex gap-2">
        <Form.Item
          className="flex-1"
          name="userId"
          rules={[
            {
              required: true,
              message: "Vui lòng chọn người dùng",
            },
          ]}
        >
          <AutoComplete
            placeholder="Start typing a name"
            allowClear
            onSearch={setKeyword}
            options={userList}
            notFoundContent={
              isLoading ? (
                <div className="flex justify-center">
                  <Spin size="small" />
                </div>
              ) : (
                "Không tìm thấy người dùng"
              )
            }
            fieldNames={{
              label: "fullName",
              value: "fullName",
            }}
            onSelect={(_, record) => setUserSelected(record?.id || null)}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={isAddLoading}>
          Thêm
        </Button>

        <Modal
          open={openModalError}
          onCancel={() => setOpenModalError(false)}
          footer={null}
          onOk={() => handleSubmit(form.getFieldsValue(), true)}
        >
          <p>{modalError}</p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpenModalError(false)}>Hủy</Button>
            <Button type="primary" onClick={() => handleSubmit(form.getFieldsValue(), true)}>
              Xác nhận
            </Button>
          </div>
        </Modal>
      </div>
    </Form>
  );
};

export default UserRoleForm;
